import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck, faTimes as faX, faEdit } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

export default function ContentReviewModal({ submission, isOpen, onClose, onApprove, onReject }) {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showEditForm, setShowEditForm] = useState(false);
  const [editedContent, setEditedContent] = useState(submission?.content || {});

  if (!submission) return null;

  const getQualityScore = () => {
    let score = 0;
    const content = submission.content || {};
    
    // Check required fields
    if (content.title) score += 20;
    if (content.description) score += 20;
    if (content.ingredients?.length > 0 || content.materials?.length > 0 || content.storyText) score += 20;
    if (content.instructions?.length > 0 || content.storyText) score += 20;
    if (content.imageUrl) score += 20;
    
    return score;
  };

  const qualityScore = getQualityScore();
  const qualityColor = qualityScore >= 80 ? 'text-green-600' : qualityScore >= 60 ? 'text-yellow-600' : 'text-red-600';

  const handleApproveClick = () => {
    if (showEditForm) {
      onApprove(submission.id, editedContent);
    } else {
      onApprove(submission.id);
    }
  };

  const handleRejectClick = () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    onReject(submission.id, rejectionReason);
  };

  const renderContentPreview = () => {
    const content = editedContent;
    const type = submission.type;

    if (type === 'recipe') {
      return (
        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Description</h4>
            <p className="text-text-primary">{content.description || 'No description'}</p>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-1">Prep Time</h4>
              <p className="text-text-primary">{content.prepTime || 'N/A'}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-1">Cook Time</h4>
              <p className="text-text-primary">{content.cookTime || 'N/A'}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-1">Serves</h4>
              <p className="text-text-primary">{content.serves || 'N/A'}</p>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Difficulty</h4>
            <p className="text-text-primary">{content.difficulty || 'N/A'}</p>
          </div>

          {content.ingredients && content.ingredients.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Ingredients</h4>
              <ul className="list-disc list-inside space-y-1">
                {content.ingredients.map((ing, idx) => (
                  <li key={idx} className="text-text-primary">
                    {ing.amount} {ing.name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {content.instructions && content.instructions.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Instructions</h4>
              <ol className="list-decimal list-inside space-y-2">
                {content.instructions.map((inst, idx) => (
                  <li key={idx} className="text-text-primary">{inst}</li>
                ))}
              </ol>
            </div>
          )}

          {content.culturalContext && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Cultural Context</h4>
              <p className="text-text-primary">{content.culturalContext}</p>
            </div>
          )}

          {content.tags && content.tags.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {content.tags.map((tag, idx) => (
                  <span key={idx} className="px-2 py-1 bg-primary/10 text-primary rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {content.attribution && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Attribution</h4>
              <p className="text-text-primary">{content.attribution}</p>
            </div>
          )}
        </div>
      );
    }

    if (type === 'activity') {
      return (
        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Description</h4>
            <p className="text-text-primary">{content.description || 'No description'}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-1">Age Range</h4>
              <p className="text-text-primary">{content.ageRange || 'N/A'}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-1">Duration/Time</h4>
              <p className="text-text-primary">{content.time || 'N/A'}</p>
            </div>
          </div>

          {content.category && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-1">Category</h4>
              <p className="text-text-primary">{content.category}</p>
            </div>
          )}

          {content.materials && content.materials.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Materials Needed</h4>
              <ul className="list-disc list-inside space-y-1">
                {content.materials.map((material, idx) => (
                  <li key={idx} className="text-text-primary">{material}</li>
                ))}
              </ul>
            </div>
          )}

          {content.instructions && content.instructions.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Instructions</h4>
              <ol className="list-decimal list-inside space-y-2">
                {content.instructions.map((inst, idx) => (
                  <li key={idx} className="text-text-primary">{inst}</li>
                ))}
              </ol>
            </div>
          )}

          {content.culturalContext && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Cultural Context</h4>
              <p className="text-text-primary">{content.culturalContext}</p>
            </div>
          )}

          {content.tags && content.tags.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {content.tags.map((tag, idx) => (
                  <span key={idx} className="px-2 py-1 bg-primary/10 text-primary rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {content.attribution && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Attribution</h4>
              <p className="text-text-primary">{content.attribution}</p>
            </div>
          )}
        </div>
      );
    }

    if (type === 'story') {
      return (
        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Description</h4>
            <p className="text-text-primary">{content.description || 'No description'}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-1">Age Range</h4>
              <p className="text-text-primary">{content.ageRange || 'N/A'}</p>
            </div>
            {content.category && (
              <div>
                <h4 className="text-sm font-semibold text-text-secondary uppercase mb-1">Category</h4>
                <p className="text-text-primary">{content.category}</p>
              </div>
            )}
          </div>

          {content.readAloud !== undefined && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-1">Read Aloud</h4>
              <p className="text-text-primary">{content.readAloud ? 'Yes' : 'No'}</p>
            </div>
          )}

          {content.content && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Story Content</h4>
              <div className="bg-surface rounded-lg p-4 border border-border">
                <p className="text-text-primary whitespace-pre-wrap font-serif leading-relaxed">
                  {content.content}
                </p>
              </div>
            </div>
          )}

          {content.culturalContext && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Cultural Context</h4>
              <p className="text-text-primary">{content.culturalContext}</p>
            </div>
          )}

          {content.tags && content.tags.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {content.tags.map((tag, idx) => (
                  <span key={idx} className="px-2 py-1 bg-primary/10 text-primary rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {content.attribution && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Attribution</h4>
              <p className="text-text-primary">{content.attribution}</p>
            </div>
          )}
        </div>
      );
    }

    // Fallback for unknown types
    return (
      <div className="space-y-6">
        <div>
          <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Description</h4>
          <p className="text-text-primary">{content.description || 'No description'}</p>
        </div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-2xl font-semibold text-text-primary">
                  Review Submission
                </h2>
                <p className="text-sm text-text-secondary mt-1">
                  {submission.type.charAt(0).toUpperCase() + submission.type.slice(1)} • Submitted {submission.submittedAt && formatDistanceToNow(submission.submittedAt.toDate(), { addSuffix: true })}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5 text-text-secondary" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Submitter Info */}
                <div className="bg-surface rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-text-secondary uppercase mb-3">Submitter Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-text-secondary">Name</p>
                      <p className="text-sm font-semibold text-text-primary">{submission.submitterName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-secondary">Submitted</p>
                      <p className="text-sm text-text-primary">
                        {submission.submittedAt && formatDistanceToNow(submission.submittedAt.toDate(), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quality Indicators */}
                <div className="bg-surface rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-text-secondary uppercase mb-3">Quality Score</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-text-primary">Completeness</span>
                        <span className={`text-sm font-semibold ${qualityColor}`}>{qualityScore}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            qualityScore >= 80 ? 'bg-green-600' : qualityScore >= 60 ? 'bg-yellow-600' : 'bg-red-600'
                          }`}
                          style={{ width: `${qualityScore}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-sm text-text-secondary">
                      {submission.content?.imageUrl ? (
                        <span className="text-green-600">✓ Image included</span>
                      ) : (
                        <span className="text-red-600">✗ No image</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Content Preview */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-text-primary">
                      {editedContent.title || 'Untitled'}
                    </h3>
                    {!showEditForm && (
                      <button
                        onClick={() => setShowEditForm(true)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      >
                        <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                        Edit
                      </button>
                    )}
                  </div>
                  
                  {showEditForm ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-text-primary mb-2">Title</label>
                        <input
                          type="text"
                          value={editedContent.title || ''}
                          onChange={(e) => setEditedContent({ ...editedContent, title: e.target.value })}
                          className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-text-primary mb-2">Description</label>
                        <textarea
                          value={editedContent.description || ''}
                          onChange={(e) => setEditedContent({ ...editedContent, description: e.target.value })}
                          rows={3}
                          className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                        />
                      </div>
                      <button
                        onClick={() => setShowEditForm(false)}
                        className="text-sm text-text-secondary hover:text-text-primary"
                      >
                        Cancel editing
                      </button>
                    </div>
                  ) : (
                    renderContentPreview()
                  )}

                  {submission.content?.imageUrl && (
                    <div className="mt-6">
                      <h4 className="text-sm font-semibold text-text-secondary uppercase mb-2">Image</h4>
                      <img
                        src={submission.content.imageUrl}
                        alt={submission.content.title}
                        className="w-full max-w-md h-64 object-cover rounded-lg border border-border"
                      />
                    </div>
                  )}
                </div>

                {/* Reject Form */}
                {showRejectForm && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-red-800 mb-2">Rejection Reason</h3>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={4}
                      placeholder="Please provide a reason for rejection..."
                      className="w-full px-4 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-between p-6 border-t border-border">
              <button
                onClick={onClose}
                className="px-6 py-2 text-text-secondary hover:text-text-primary transition-colors"
              >
                Cancel
              </button>
              <div className="flex gap-3">
                {!showRejectForm ? (
                  <>
                    <button
                      onClick={() => setShowRejectForm(true)}
                      className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <FontAwesomeIcon icon={faX} className="w-4 h-4" />
                      Reject
                    </button>
                    <button
                      onClick={handleApproveClick}
                      className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <FontAwesomeIcon icon={faCheck} className="w-4 h-4" />
                      {showEditForm ? 'Approve with Changes' : 'Approve'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setShowRejectForm(false);
                        setRejectionReason('');
                      }}
                      className="px-6 py-2 text-text-secondary hover:text-text-primary transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRejectClick}
                      className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <FontAwesomeIcon icon={faX} className="w-4 h-4" />
                      Confirm Rejection
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

